import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { decryptIdFromUrl, encryptIdForUrl } from "@/utils/urlCrypto";
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Shield,
  Edit2,
  Archive,
  RotateCcw,
  Loader2,
  UserX,
  CheckCircle,
  XCircle,
  Link as LinkIcon,
  FileText,
  Clock,
  AlertCircle,
  ImageIcon,
  History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { cn } from "@/lib/utils";
import { resolveMediaUrl } from "@/lib/media";
import { SolicitacaoStatusBadge } from "@/components/solicitacoes/SolicitacaoStatusBadge";
import type { StatusSolicitacao } from "@/types/solicitacao";
import { AppConfig } from "@/datasources/AppConfig";
import { OUVIDORIA_ENDPOINTS } from "@/config";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";

interface FotoPerfil {
  id: string;
  arquivo: string;
}

interface Agente {
  id: string;
  nome: string;
  email?: string | null;
  telefone?: string | null;
  fotoPerfil?: FotoPerfil | null;
  intergracao?: string | null;
  arquivado: boolean;
  status?: string;
  gestor?: boolean;
}

const AGENTE_STATUS_LABELS: Record<string, string> = {
  "Aprovado": "Aprovado",
  "Reprovado": "Reprovado",
  "Aguardando Aprovação": "Aguardando Aprovação",
};

interface Anexo {
  id: string;
  arquivo: string;
}

interface SolicitacaoAgente {
  id: string;
  topico: {
    id: string;
    descricao: string;
  };
  upn: string;
  status: string;
  estadoAprovacao: string;
  dataCriacao: string;
  totalApoiadores: number;
  arquivada: boolean;
  anexos?: Anexo[];
}


export default function AgenteDetalhesPage() {
  const { id: encryptedId } = useParams<{ id: string }>();
  const [decryptedId, setDecryptedId] = useState<string>("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isGestor, status: currentAgenteStatus } = useAgentePermissions();
  
  const [agente, setAgente] = useState<Agente | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState<string>("");
  
  const [editForm, setEditForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    fotoPerfil: "",
  });
  
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoAgente[]>([]);
  const [isLoadingSolicitacoes, setIsLoadingSolicitacoes] = useState(false);

  // Decrypt ID from URL
  useEffect(() => {
    const decryptId = async () => {
      if (encryptedId) {
        const decrypted = await decryptIdFromUrl(encryptedId);
        setDecryptedId(decrypted);
      }
    };
    decryptId();
  }, [encryptedId]);

  const fetchAgente = async () => {
    if (!decryptedId) {
      setError("ID do agente não informado");
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      
      // Buscar lista de agentes e filtrar pelo ID (não existe endpoint GET individual)
      // Usa PageSize grande para garantir que todos os agentes sejam retornados
      const result = await dataSource.get<Record<string, unknown>[]>({
        endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes/${decryptedId}`,
        headers: { 'Accept': 'application/vnd.data.full.v1+json' },
        forceRefresh: true,
 
      });

      if ('failure' in result) {
        const errorMsg = result.failure.messages?.join(", ") || "Erro ao carregar agente";
        setError(errorMsg);
        return;
      }

      const agentes = result.success.body as Record<string, unknown>[];
      const raw = agentes.find(a => a.id === decryptedId);
      
      if (!raw) {
        setError("Agente não encontrado");
        return;
      }
      
      const agenteData: Agente = {
        id: String(raw.id || ''),
        nome: String(raw.nome || ''),
        email: (raw.email as string) || null,
        telefone: (raw.telefone as string) || null,
        fotoPerfil: (raw.fotoPerfil as Record<string, unknown>) ? {
          id: ((raw.fotoPerfil as Record<string, unknown>).id as string) || '',
          arquivo: ((raw.fotoPerfil as Record<string, unknown>).arquivo as string) || '',
        } : null,
        intergracao: (raw.intergracao as string) || null,
        arquivado: Boolean(raw.arquivado),
        status: (raw.status as string) || null,
        gestor: Boolean(raw.gestor),
      };
      
      setAgente(agenteData);
      setEditForm({
        nome: agenteData.nome,
        email: agenteData.email || "",
        telefone: agenteData.telefone || "",
        fotoPerfil: agenteData.fotoPerfil?.arquivo || "",
      });
    } catch (err) {
      setError("Erro ao carregar dados do agente: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSolicitacoesAgente = async () => {
    if (!decryptedId) return;
    
    setIsLoadingSolicitacoes(true);
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const result = await dataSource.get<SolicitacaoAgente[]>({
        endpoint: `solicitacoes?agenteId=${decryptedId}&ouvidoriaId=${AppConfig.ouvidoriaId}`,
        headers: { 'Accept': 'application/vnd.data.partial.v2+json' },
        queryParams: { PageSize: 1000 },
        forceRefresh: true,
        serialize: (raw: unknown) => {
          if (Array.isArray(raw)) return raw as SolicitacaoAgente[];
          const data = raw as Record<string, unknown>;
          if (data?.items && Array.isArray(data.items)) return data.items as SolicitacaoAgente[];
          if (data?.data && Array.isArray(data.data)) return data.data as SolicitacaoAgente[];
          return [];
        },
      });

      if ('failure' in result) {
        return;
      }

      const data = result.success.data ?? (result.success.body as SolicitacaoAgente[]);
      setSolicitacoes(Array.isArray(data) ? data : []);
    } catch (err) {
      // Silenciar erro de carregamento de solicitações
    } finally {
      setIsLoadingSolicitacoes(false);
    }
  };


  useEffect(() => {
    fetchAgente();
    fetchSolicitacoesAgente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decryptedId]);

  // Sempre que a página ganhar foco ou voltar à aba, recarrega os dados
  useEffect(() => {
    const handleFocus = () => {
      fetchAgente();
      fetchSolicitacoesAgente();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchAgente();
        fetchSolicitacoesAgente();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decryptedId]);


  const handleSave = async () => {
    if (!decryptedId) return;
    
    setIsSaving(true);
    try {
      const token = AppConfig.tokenAcesso;
      
      // 1. Se a foto de perfil foi alterada, chamar o endpoint específico para atualizar foto
      const fotoChanged = editForm.fotoPerfil !== (agente?.fotoPerfil?.arquivo || "");
      if (fotoChanged && editForm.fotoPerfil) {
        try {
          const fotoFormData = new FormData();
          
          // Se for base64, converter para Blob
          if (editForm.fotoPerfil.startsWith('data:')) {
            const base64Data = editForm.fotoPerfil.split(',')[1];
            const mimeType = editForm.fotoPerfil.split(',')[0].match(/:(.*?);/)?.[1] || 'image/png';
            const byteCharacters = atob(base64Data);
            const byteArrays = [];
            const sliceSize = 512;
            
            for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
              const slice = byteCharacters.slice(offset, offset + sliceSize);
              const byteNumbers = new Array(slice.length);
              for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              byteArrays.push(byteArray);
            }
            
            const blob = new Blob(byteArrays, { type: mimeType });
            fotoFormData.append("arquivo", blob, "foto.png");
          } else {
            // Se for URL, não suportado - precisa ser arquivo
            throw new Error("A foto de perfil deve ser um arquivo, não uma URL");
          }
          
          fotoFormData.append("date", String(Date.now()));
          
          const fotoUrl = `${Settings.urlBase}/urbnet/v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes/${decryptedId}/atualizar-foto-perfil`;
          
          const fotoResponse = await fetch(fotoUrl, {
            method: "POST",
            headers: {
              "Accept": "application/vnd.data.full.v1+json",
              "Authorization": `Bearer ${token}`,
            },
            body: fotoFormData,
          });

          if (!fotoResponse.ok) {
            const errorData = await fotoResponse.json().catch(() => ({}));
            throw new Error(errorData.message || errorData.detail || `Erro ${fotoResponse.status}: ${fotoResponse.statusText}`);
          }
        } catch (error) {
          throw new Error("Erro ao atualizar a foto de perfil: " + (error instanceof Error ? error.message : "Erro desconhecido"));
        }
      }
      
      // 2. Atualizar os outros dados do agente (sem a foto)
      // Verifica se há mudanças nos outros campos além da foto
      const nomeChanged = editForm.nome !== agente?.nome;
      const emailChanged = editForm.email !== agente?.email;
      const telefoneChanged = editForm.telefone !== agente?.telefone;
      const hasOtherChanges = nomeChanged || emailChanged || telefoneChanged;
      
      // Se só a foto mudou, não executa o PATCH
      if (!hasOtherChanges && fotoChanged) {
        // Apenas a foto de perfil foi alterada, não executando PATCH
        toast({
          title: "Agente atualizado",
          description: "A foto de perfil foi atualizada.",
        });
        setIsEditing(false);
        fetchAgente();
        return;
      }
      
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const result = await dataSource.patch({
        endpoint: OUVIDORIA_ENDPOINTS.atualizarAgente(decryptedId),
        body: {
          nome: editForm.nome,
          email: editForm.email || null,
          telefone: editForm.telefone || null,
        },
        headers: { 'Accept': 'application/vnd.data.full.v1+json' },
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao atualizar",
          description: result.failure.messages.join(", ") || "Erro ao atualizar agente",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Agente atualizado",
        description: "Os dados foram salvos com sucesso.",
      });
      
      setIsEditing(false);
      fetchAgente();
    } catch (err) {
      toast({
        title: "Erro ao atualizar",
        description: err instanceof Error ? err.message : "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!decryptedId || !agente) return;
    
    setIsSaving(true);
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const endpoint = agente.arquivado 
        ? OUVIDORIA_ENDPOINTS.restaurarAgente(decryptedId)
        : OUVIDORIA_ENDPOINTS.arquivarAgente(decryptedId);
      
      const result = await dataSource.post({
        endpoint,
        body: {},
      });

      if ('failure' in result) {
        toast({
          title: "Erro",
          description: result.failure.messages.join(", ") || "Erro ao processar ação",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: agente.arquivado ? "Agente restaurado" : "Agente arquivado",
        description: agente.arquivado 
          ? "O agente foi restaurado com sucesso."
          : "O agente foi arquivado com sucesso.",
      });
      
      setShowArchiveDialog(false);
      fetchAgente();
    } catch (err) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAlterarStatus = async () => {
    if (!decryptedId || !newStatus) return;
    
    setIsSaving(true);
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const endpoint = OUVIDORIA_ENDPOINTS.alterarStatusAgente(decryptedId);
      
      const result = await dataSource.post({
        endpoint,
        queryParams: { status: newStatus },
        headers: { 'Accept': 'application/vnd.data.full.v1+json' },
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao alterar status",
          description: result.failure.messages.join(", ") || "Erro ao alterar status do agente",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Status alterado",
        description: `O status do agente foi alterado para "${newStatus}" com sucesso.`,
      });
      
      setShowStatusDialog(false);
      fetchAgente();
    } catch (err) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro inesperado ao alterar o status.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAtribuirGestor = async () => {
    if (!decryptedId || !agente) return;
    
    if (!confirm("Tem certeza que deseja atribuir o papel de gestor a este agente?")) {
      return;
    }

    setIsSaving(true);
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const endpoint = OUVIDORIA_ENDPOINTS.atribuirGestor(decryptedId);
      
      const result = await dataSource.post({
        endpoint,
        headers: { 'Accept': 'application/vnd.data.full.v1+json' },
        body: {},
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao atribuir gestor",
          description: result.failure.messages.join(", ") || "Erro ao atribuir gestor ao agente",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Gestor atribuído",
        description: "O agente foi promovido a gestor com sucesso.",
      });
      
      fetchAgente();
    } catch (err) {
      toast({
        title: "Erro ao atribuir gestor",
        description: err instanceof Error ? err.message : "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Verifica se o usuário atual pode alterar o status do agente
  const canAlterStatus = isGestor && !agente?.arquivado && decryptedId !== AppConfig.agenteId;

  // Verifica se o usuário atual pode atribuir gestor ao agente
  const canAtribuirGestor = isGestor && !agente?.arquivado && decryptedId !== AppConfig.agenteId && !agente?.gestor;

  const handleSolicitacaoClick = async (id: string) => {
    const encryptedId = await encryptIdForUrl(id);
    navigate(`/solicitacoes/${encryptedId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando dados do agente...</p>
        </div>
      </div>
    );
  }

  if (error || !agente) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/agentes")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para Agentes
        </Button>
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground space-y-4 px-4">
          <div className="text-center space-y-2">
            <h3 className="text-xl font-semibold text-foreground">Agente não encontrado</h3>
            <p className="text-muted-foreground">
              {error || "O agente solicitado não existe ou foi removido."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const avatarFallback = agente.nome ? agente.nome.slice(0, 2).toUpperCase() : "AG";

  // Helper function to get status badge color
  const getStatusBadgeColor = (status?: string) => {
    if (!status) return "default";
    switch (status) {
      case "Aprovado":
        return "bg-success/10 text-success border-success/20";
      case "Reprovado":
        return "bg-destructive/10 text-destructive border-destructive/20";
      case "Aguardando Aprovação":
        return "bg-warning/10 text-warning border-warning/20";
      default:
        return "default";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate("/agentes")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para Agentes
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <Avatar className="h-24 w-24 mb-4">
                  {agente.fotoPerfil?.arquivo ? (
                    <AvatarImage src={resolveMediaUrl(agente.fotoPerfil.arquivo) || agente.fotoPerfil.arquivo} alt={agente.nome} />
                  ) : null}
                  <AvatarFallback className="text-2xl bg-gradient-to-br from-primary/20 to-secondary/20">
                    {avatarFallback}
                  </AvatarFallback>
                </Avatar>
                
                <h2 className="text-xl font-semibold">{agente.nome}</h2>
                
                <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
                  <Badge
                    variant={agente.status === "Reprovado" ? "destructive" : "default"}
                    className={cn(
                      agente.status === "Aprovado" && "bg-success/10 text-success border-success/20",
                      agente.status === "Aguardando Aprovação" && "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    )}
                  >
                    {agente.status === "Reprovado" ? (
                      <>
                        <XCircle className="h-3 w-3 mr-1" />
                        Reprovado
                      </>
                    ) : agente.status === "Aguardando Aprovação" ? (
                      <>
                        <Clock className="h-3 w-3 mr-1" />
                        Pendente
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Ativo
                      </>
                    )}
                  </Badge>
                  
                  {agente.status && (
                    <Badge
                      variant="outline"
                      className={getStatusBadgeColor(agente.status)}
                    >
                      {agente.status}
                    </Badge>
                  )}
                  
                  {agente.gestor && (
                    <span className="inline-flex items-center rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 text-primary border border-primary/30 font-semibold text-xs px-2 py-0.5 shadow-sm">
                      <Shield className="h-3 w-3 mr-1 text-primary" />
                      Gestor
                    </span>
                  )}
                </div>

                <Separator className="my-4" />

                <div className="w-full space-y-3 text-left">
                  {agente.email && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Mail className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">E-mail</p>
                        <p className="text-sm font-medium">{agente.email}</p>
                      </div>
                    </div>
                  )}
                  
                  {agente.telefone && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Phone className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Telefone</p>
                        <p className="text-sm font-medium">{agente.telefone}</p>
                      </div>
                    </div>
                  )}

                  {agente.intergracao && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <LinkIcon className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Integração</p>
                        <p className="text-sm font-medium">{agente.intergracao}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Details / Edit Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle>
                {isEditing ? "Editar Informações" : "Informações do Agente"}
              </CardTitle>
              <CardDescription>
                {isEditing
                  ? "Atualize os dados do agente abaixo"
                  : "Detalhes completos do agente"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome completo</Label>
                    <Input
                      id="nome"
                      value={editForm.nome}
                      onChange={(e) =>
                        setEditForm({ ...editForm, nome: e.target.value })
                      }
                      placeholder="Nome do agente"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      type="email"
                      value={editForm.email}
                      onChange={(e) =>
                        setEditForm({ ...editForm, email: e.target.value })
                      }
                      placeholder="email@exemplo.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="telefone">Telefone</Label>
                    <Input
                      id="telefone"
                      value={editForm.telefone}
                      onChange={(e) =>
                        setEditForm({ ...editForm, telefone: e.target.value })
                      }
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="fotoPerfil">URL da foto de perfil</Label>
                    <Input
                      id="fotoPerfil"
                      value={editForm.fotoPerfil}
                      onChange={(e) =>
                        setEditForm({ ...editForm, fotoPerfil: e.target.value })
                      }
                      placeholder="https://exemplo.com/foto.jpg"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        setEditForm({
                          nome: agente.nome || "",
                          email: agente.email || "",
                          telefone: agente.telefone || "",
                          fotoPerfil: agente.fotoPerfil?.arquivo || "",
                        });
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        "Salvar alterações"
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Nome</p>
                      <p className="font-medium">{agente.nome || "-"}</p>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Status</p>
                      <Badge
                        variant="outline"
                        className={getStatusBadgeColor(agente.status)}
                      >
                        {agente.status || "Não definido"}
                      </Badge>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Status do Agente</p>
                      <Badge
                        variant={agente.arquivado ? "destructive" : "default"}
                        className={cn(
                          !agente.arquivado && "bg-success/10 text-success border-success/20"
                        )}
                      >
                        {agente.arquivado ? "Arquivado" : "Ativo"}
                      </Badge>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Papel</p>
                      {agente.gestor ? (
                        <span className="inline-flex items-center rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 text-primary border border-primary/30 font-semibold text-xs px-2 py-0.5 shadow-sm">
                          <Shield className="h-3 w-3 mr-1 text-primary" />
                          Gestor
                        </span>
                      ) : (
                        <Badge variant="outline">Agente</Badge>
                      )}
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">E-mail</p>
                      <p className="font-medium">{agente.email || "-"}</p>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Telefone</p>
                      <p className="font-medium">{agente.telefone || "-"}</p>
                    </div>
                    
                    {agente.intergracao && (
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Integração</p>
                        <p className="font-medium">{agente.intergracao}</p>
                      </div>
                    )}
                  </div>

                  {/* Status Change Button - Only for gestors and approved agents */}
                  {canAlterStatus && agente.status && (
                    <div className="pt-4 border-t">
                      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="gap-2">
                            <History className="h-4 w-4" />
                            Alterar Status
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Alterar Status</DialogTitle>
                            <DialogDescription>
                              Selecione o novo status para este agente.
                            </DialogDescription>
                          </DialogHeader>
                          <Select
                            value={newStatus}
                            onValueChange={setNewStatus}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o status" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(AGENTE_STATUS_LABELS).map(([value, label]) => (
                                <SelectItem key={value} value={value}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setShowStatusDialog(false)}
                              disabled={isSaving}
                            >
                              Cancelar
                            </Button>
                            <Button onClick={handleAlterarStatus} disabled={!newStatus || isSaving}>
                              {isSaving ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Alterando...
                                </>
                              ) : (
                                "Confirmar"
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}

                  {/* Atribuir Gestor Button - Only for gestors */}
                  {canAtribuirGestor && (
                    <div className="pt-4 border-t">
                      <Button variant="outline" className="gap-2" onClick={handleAtribuirGestor} disabled={isSaving}>
                        {isSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Atribuindo...
                          </>
                        ) : (
                          <>
                            <Shield className="h-4 w-4" />
                            Atribuir Gestor
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Solicitações Atribuídas - Full Width */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Solicitações Atribuídas
            </CardTitle>
            <CardDescription>
              Solicitações atribuídas a este agente
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingSolicitacoes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : solicitacoes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground/50 mb-2" />
                <p className="text-muted-foreground">Nenhuma solicitação atribuída</p>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {solicitacoes.map((solicitacao) => {
                  const primeiroAnexo = solicitacao.anexos?.[0]?.arquivo;
                  
                  // Mapear status da API para o tipo StatusSolicitacao
                  const mapStatus = (status: string): StatusSolicitacao => {
                    const statusLower = status.toLowerCase();
                    if (statusLower === "emanalise" || statusLower === "em analise") return "EmAnalise";
                    if (statusLower === "resolvida" || statusLower === "concluida") return "resolvida";
                    if (statusLower === "agendada") return "agendada";
                    if (statusLower === "aprovada") return "aprovada";
                    if (statusLower === "reprovada") return "reprovada";
                    if (statusLower === "arquivada") return "arquivada";
                    return "pendente";
                  };
                  
                  return (
                    <div
                      key={solicitacao.id}
                      className="flex gap-3 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                      onClick={() => handleSolicitacaoClick(solicitacao.id)}
                    >
                      {/* Thumbnail */}
                      <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                        {primeiroAnexo ? (
                          <img 
                            src={primeiroAnexo} 
                            alt="Anexo" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="h-6 w-6 text-muted-foreground/50" />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex flex-col flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-medium text-sm truncate">
                            {solicitacao.upn}
                          </span>
                          <SolicitacaoStatusBadge 
                            status={mapStatus(solicitacao.status)} 
                            estadoAprovacao={solicitacao.estadoAprovacao}
                            className="shrink-0"
                          />
                        </div>
                        <Badge variant="outline" className="text-xs w-fit mb-2">
                          {solicitacao.topico?.descricao || "Sem tópico"}
                        </Badge>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(solicitacao.dataCriacao).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
